import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FaTruck, FaMapMarkedAlt, FaRoute, FaCalendarDay, FaDirections, FaExternalLinkAlt } from 'react-icons/fa';

// Fix Leaflet icon issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const ShopIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const AdminMap = ({ appointments }) => {
    const [markers, setMarkers] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(false);
    const [isOptimized, setIsOptimized] = useState(false);

    const SHOP_COORDS = [49.0067, 1.9100]; // Meulan-en-Yvelines / Shop Base

    // Utility: calculate distance between two points
    const getDistance = (p1, p2) => {
        return Math.sqrt(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2));
    };

    useEffect(() => {
        const geocodeAppointments = async () => {
            setLoading(true);
            const resolved = [];
            // Candidates with address
            const candidates = appointments.filter(a => a.adresse && a.statut !== 'refuse');

            for (const apt of candidates) {
                try {
                    // Cache check would be better, but for demo:
                    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(apt.adresse)}`);
                    const data = await response.json();
                    if (data && data.length > 0) {
                        resolved.push({
                            ...apt,
                            position: [parseFloat(data[0].lat), parseFloat(data[0].lon)]
                        });
                    }
                } catch (e) { console.error("Geocode error", e); }
                await new Promise(r => setTimeout(r, 600)); // Delay for Nominatim
            }

            setMarkers(resolved);
            setLoading(false);
        };

        if (appointments.length > 0) {
            geocodeAppointments();
        }
    }, [appointments]);

    // Filter markers for the selected day
    const dayAppointments = useMemo(() => {
        const filtered = markers.filter(m => m.date_heure.startsWith(selectedDate));

        if (isOptimized) {
            // Simple Nearest Neighbor Heuristic
            const unvisited = [...filtered];
            const optimized = [];
            let currentPos = SHOP_COORDS;

            while (unvisited.length > 0) {
                let closestIdx = 0;
                let minDist = getDistance(currentPos, unvisited[0].position);

                for (let i = 1; i < unvisited.length; i++) {
                    const d = getDistance(currentPos, unvisited[i].position);
                    if (d < minDist) {
                        minDist = d;
                        closestIdx = i;
                    }
                }

                const next = unvisited.splice(closestIdx, 1)[0];
                optimized.push(next);
                currentPos = next.position;
            }
            return optimized;
        } else {
            // Chronological order
            return [...filtered].sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure));
        }
    }, [markers, selectedDate, isOptimized]);

    const tourPath = dayAppointments.length > 0
        ? [SHOP_COORDS, ...dayAppointments.map(m => m.position), SHOP_COORDS]
        : [];

    const openGoogleMapsTour = () => {
        if (dayAppointments.length === 0) return;
        const base = "https://www.google.com/maps/dir/";
        const shop = `${SHOP_COORDS[0]},${SHOP_COORDS[1]}`;
        const waypoints = dayAppointments.map(a => `${a.position[0]},${a.position[1]}`).join("/");
        window.open(`${base}${shop}/${waypoints}/${shop}`, '_blank');
    };

    return (
        <div className="admin-map-container" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Header / Controls */}
            <div className="admin-section fade-in" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
                    <div>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <FaMapMarkedAlt style={{ color: '#2563eb' }} /> Router Intelligent
                        </h2>
                        <p className="text-muted">Optimisez vos déplacements en Ile-de-France.</p>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <div className="input-field" style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f8fafc', padding: '8px 15px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <FaCalendarDay style={{ color: '#64748b' }} />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: 'bold' }}
                            />
                        </div>

                        <button
                            className={`btn-files ${isOptimized ? 'active' : ''}`}
                            onClick={() => setIsOptimized(!isOptimized)}
                            style={{
                                background: isOptimized ? '#2563eb' : '#fff',
                                color: isOptimized ? '#fff' : '#2563eb',
                                border: '1px solid #2563eb',
                                padding: '10px 15px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <FaRoute /> {isOptimized ? 'Désactiver Optimisation' : 'Optimiser le trajet'}
                        </button>

                        <button
                            className="btn-approve"
                            onClick={openGoogleMapsTour}
                            disabled={dayAppointments.length === 0}
                            style={{ padding: '10px 15px', borderRadius: '10px', gap: '8px' }}
                        >
                            <FaDirections /> GPS Complet
                        </button>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 350px) 1fr', gap: '20px', height: '650px' }}>
                {/* Sidebar Itinerary */}
                <div className="admin-section" style={{ overflowY: 'auto' }}>
                    <h3 style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
                        Ordre de passage {isOptimized && '✨'}
                    </h3>

                    {loading && <p>Calcul de la carte...</p>}

                    {!loading && dayAppointments.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
                            <FaTruck size={40} style={{ opacity: 0.3, marginBottom: '15px' }} />
                            <p>Aucun rendez-vous prévu pour cette date.</p>
                        </div>
                    )}

                    <div className="itinerary-list">
                        {dayAppointments.length > 0 && (
                            <div className="itinerary-step" style={{ display: 'flex', gap: '15px', marginBottom: '20px', position: 'relative' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#ef4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>H</div>
                                    <div style={{ width: '2px', flex: 1, background: '#e2e8f0', margin: '5px 0' }}></div>
                                </div>
                                <div>
                                    <h4 style={{ margin: 0 }}>Départ : Boutique / Base</h4>
                                    <p style={{ margin: '5px 0', fontSize: '0.85rem', color: '#64748b' }}>Meulan-en-Yvelines</p>
                                </div>
                            </div>
                        )}

                        {dayAppointments.map((apt, idx) => (
                            <div key={apt.id} className="itinerary-step" style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#2563eb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>{idx + 1}</div>
                                    <div style={{ width: '2px', flex: 1, background: '#e2e8f0', margin: '5px 0' }}></div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <h4 style={{ margin: 0 }}>{apt.client_nom}</h4>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#2563eb' }}>{new Date(apt.date_heure).getHours()}h{new Date(apt.date_heure).getMinutes() || '00'}</span>
                                    </div>
                                    <p style={{ margin: '5px 0', fontSize: '0.85rem', color: '#64748b' }}>{apt.adresse}</p>
                                    <button
                                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(apt.adresse)}`, '_blank')}
                                        style={{ border: 'none', background: 'none', color: '#3b82f6', fontSize: '0.75rem', fontWeight: 'bold', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}
                                    >
                                        <FaExternalLinkAlt size={10} /> Aller au RDV
                                    </button>
                                </div>
                            </div>
                        ))}

                        {dayAppointments.length > 0 && (
                            <div className="itinerary-step" style={{ display: 'flex', gap: '15px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <div style={{ width: '30px', height: '30px', borderRadius: '50%', border: '2px solid #ef4444', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>R</div>
                                </div>
                                <div>
                                    <h4 style={{ margin: 0 }}>Retour : Boutique</h4>
                                    <p style={{ margin: '5px 0', fontSize: '0.85rem', color: '#64748b' }}>Fin de tournée</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Map Area */}
                <div className="admin-section" style={{ padding: 0, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <MapContainer center={SHOP_COORDS} zoom={11} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />

                        {/* Shop Marker */}
                        <Marker position={SHOP_COORDS} icon={ShopIcon}>
                            <Popup><strong>Base RoulezPropre</strong><br />Départ/Arrivée</Popup>
                        </Marker>

                        {/* Appointments Markers */}
                        {markers.map(apt => {
                            const isToday = apt.date_heure.startsWith(selectedDate);
                            return (
                                <Marker
                                    key={apt.id}
                                    position={apt.position}
                                    opacity={isToday ? 1 : 0.3}
                                >
                                    <Popup>
                                        <strong>{apt.client_nom}</strong><br />
                                        {new Date(apt.date_heure).toLocaleString()}<br />
                                        {apt.prestation}
                                    </Popup>
                                </Marker>
                            );
                        })}

                        {/* Tour Route */}
                        {tourPath.length > 1 && (
                            <Polyline
                                positions={tourPath}
                                color={isOptimized ? "#10b981" : "#2563eb"}
                                weight={isOptimized ? 4 : 3}
                                dashArray={isOptimized ? "" : "10, 10"}
                            />
                        )}
                    </MapContainer>
                </div>
            </div>
        </div>
    );
};

export default AdminMap;

